/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
  Align,
  Arrange,
  Button,
  Icon,
  Link,
  Spacer,
  SvgClock,
  SvgInfo,
  Text,
  TextButton,
  Toast,
} from '@loomhq/lens';
import _pick from 'lodash/pick';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  updateShareVideoDownloadUrl,
  updateShareVideoInTrimMode,
} from 'actions/shareVideo';
import { EDIT_SAVE_CHANGE, TRIM_HELP_ARTICLE_CLICKED } from 'constants/events';
import { MANAGE_PLANS } from 'constants/routes';
import { fetchShareVideoDownloadUrlCreator } from 'creators/shareVideo';
import { leaveEditModeCreator } from 'creators/trimming';
import { updateVideoPlayerDurationCreator } from 'creators/videoPlayer';
import { selectVideo } from 'selectors/videoPlayer';
import * as analytics from 'utilities/analytics';
import { secondsToVideoTS } from 'utilities/time';

import { Video } from 'types/clips';

import SaveConflictModal from './save-conflict-modal';
import { useClipsState } from './clips/state';
import { UpdateVideoClips } from './graphql/UpdateVideoClips.gql';

import styles from './styles.module.less';
import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';

const HELP_ARTICLE_LABEL = 'View help article';
const SAVE_BUTTON_LABEL = 'Save changes';
const TRIM_INSTRUCTIONS_LABEL =
  'Trim your video by dragging the handles and using the Split button.';

type Props = React.PropsWithChildren<{
  containerRef: (element: HTMLDivElement) => void;
  duration: number;
  durationChanged?: boolean;
  hasBeenUpdated?: boolean;
  isOverVideoDurationLimits?: boolean;
  onHasTrims: () => boolean;
  onExitWithoutSaving: () => void;
  trimVideoDuration: number;
  videoId: string;
}>;

const EditInformation = ({
  duration,
  durationChanged,
  hasBeenUpdated,
  isOverVideoDurationLimits,
  onHasTrims,
  onExitWithoutSaving,
  containerRef,
  trimVideoDuration,
  videoId,
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const video: Video = useSelector(selectVideo);

  const lastTrimId = video?.processing_information?.trim_id;

  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showSaveConflictModal, setShowSaveConflictModal] = useState(false);

  const { clips, resetState, setNotifiedUsers, setShowWeaveToast } =
    useClipsState(state => ({
      clips: state.clips,
      resetState: state.resetState,
      setNotifiedUsers: state.setNotifiedUsers,
      setShowWeaveToast: state.setShowWeaveToast,
    }));

  const [clipsData, setClipsData] = useState(clips);

  useEffect(() => {
    const updatedClips = clips.map(clip => ({
      id: clip.id,
      // @ts-ignore
      position: clip.position,
      // @ts-ignore
      trim_ranges: clip.trimRanges.map(range => _pick(range, ['from', 'to'])),
    }));

    // @ts-ignore
    setClipsData(updatedClips);
  }, [clips]);

  const [updateVideoClips, { loading }] = useMutation(UpdateVideoClips, {
    fetchPolicy: 'no-cache',
  });

  const cancelChanges = () => {
    resetState();
    onExitWithoutSaving?.();
  };

  const saveChanges = async (forceSave = false) => {
    analytics.track(EDIT_SAVE_CHANGE, {
      videoId: video.id,
      clipsCount: clips.length,
      duration,
    });

    try {
      const response = await updateVideoClips({
        variables: {
          videoId,
          clips: clipsData,
          forceSave,
          lastTrimId,
        },
      });
      const responseType = response?.data?.updateVideoClips?.__typename;

      if (responseType === 'SavingOverNewClipChangesPayload') {
        setShowSaveConflictModal(true);

        return;
      }

      if (responseType === 'UpdateVideoClipsPayload') {
        // @ts-ignore
        const patchedVideo = response?.video;
        // @ts-ignore
        const respNotifiedUsers = response?.notifiedUsers;

        dispatch(leaveEditModeCreator(patchedVideo));
        dispatch(updateVideoPlayerDurationCreator(duration));
        dispatch(updateShareVideoInTrimMode(false));
        dispatch(updateShareVideoDownloadUrl(null));
        dispatch(fetchShareVideoDownloadUrlCreator());
        resetState();

        setNotifiedUsers(respNotifiedUsers);
        setShowWeaveToast(true);
      } else if (responseType) {
        // something went wrong, this must be an error return type.
        // @ts-ignore
        console.error('Save failed:', response.message);
        setShowErrorToast(true);
      }
    } catch (err) {
      // we're not going to show the raw error details, but we
      // can at least log it in the console.
      console.error('Save failed:', err);
      setShowErrorToast(true);
    }
  };

  const forceSaveChanges = () => {
    saveChanges(true);
  };

  return (
    <div className={styles.editInformation} ref={containerRef}>
      <Toast
        isOpen={showErrorToast}
        onCloseClick={() => setShowErrorToast(false)}
      >
        Sorry. Something went wrong trying to save the changes.
      </Toast>
      <SaveConflictModal
        isOpen={showSaveConflictModal}
        setIsOpen={setShowSaveConflictModal}
        onOverwrite={forceSaveChanges}
      />
      <Align alignment="center">
        <div className={styles.durationChange}>
          {durationChanged ? (
            <Arrange gap="xsmall">
              <Icon color="bodyDimmed" icon={<SvgClock />} />{' '}
              <Text
                size="medium"
                color="bodyDimmed"
                fontWeight="medium"
                className={styles.timeDisplay}
              >
                {secondsToVideoTS(duration, 1)}
              </Text>{' '}
              <Text size="medium" color="bodyDimmed" fontWeight="medium">
                &rarr;
              </Text>
              <Text
                size="medium"
                color="body"
                fontWeight="medium"
                className={styles.timeDisplay}
              >
                {secondsToVideoTS(trimVideoDuration, 1)}
              </Text>
            </Arrange>
          ) : (
            <Text size="medium">{TRIM_INSTRUCTIONS_LABEL}</Text>
          )}
        </div>

        {isOverVideoDurationLimits ? (
          <Arrange alignContent="start" gap="xsmall" justifyItems="start">
            <Text color="red">Your video is over the 5 minute limit.</Text>
            <Text color="bodyDimmed">Trim to save your changes, or</Text>
            <Link href={MANAGE_PLANS}>upgrade</Link>
            <Text color="bodyDimmed">for unlimited recording length.</Text>
          </Arrange>
        ) : (
          <TextButton
            size="small"
            icon={<Icon size={2} icon={<SvgInfo />} color="primary" />}
            htmlTag="a"
            // @ts-expect-error removed lens.d.ts
            target="_blank"
            href="https://support.loom.com/hc/en-us/articles/360002217478-How-to-trim-your-Loom-video-"
            onClick={() =>
              analytics.track(TRIM_HELP_ARTICLE_CLICKED, {
                id: videoId,
                has_been_trimmed: onHasTrims,
                event_type: 'click',
              })
            }
          >
            <Text color="primary" size="small" fontWeight="medium">
              {HELP_ARTICLE_LABEL}
            </Text>
          </TextButton>
        )}

        <Spacer top="medium" />
        <Arrange gap="small">
          <Button id="cancelButton" onClick={cancelChanges}>
            Cancel
          </Button>
          <Button
            hasLoader={loading}
            id="saveChangesButton"
            variant="primary"
            isDisabled={isOverVideoDurationLimits || !hasBeenUpdated || loading}
            onClick={() => saveChanges()}
          >
            {SAVE_BUTTON_LABEL}
          </Button>
        </Arrange>
      </Align>
    </div>
  );
};

export default EditInformation;
